/**
 * DC isolator compatibility depends on runtime context (the specific string/MPPT path)
 * and a voltage_to_current_rating map that is inconsistently present in API results.
 *
 * This guard runs after fetch to:
 * 1) resolve the actual dc_isolator slot being edited,
 * 2) rehydrate missing rating maps from local AccountHelper data by component code,
 * 3) apply the same compatibility check used in electrical validation/auto-fill.
 *
 * This keeps modal options aligned with what refresh/validation considers valid.
 */
import { getDCIsolatorCompatibility } from 'elements/electrical/helpers'
import { getSlotKey, getSlotsByLocation } from 'projectSections/sections/design/systems/Slots'
import type { OtherComponentType } from 'types/otherComponent'
import { parseJsonSafe } from 'util/misc'
import type { PostFetchGuard } from '../types'

type RatingMap = Record<string, number>

type FetchedComponent = {
  uuid?: string
  code?: string
  data?: string
  voltage_to_current_rating?: string | RatingMap
  other?: { voltage_to_current_rating?: string | RatingMap }
}

type LocalComponent = {
  code?: string
  voltage_to_current_rating?: string | RatingMap
}

const resolveSlotKeyFromTarget = ({
  slotKey,
  targetUuids,
}: {
  slotKey?: string
  targetUuids?: string[]
}): string | undefined => {
  if (slotKey?.startsWith('dc_isolator_')) return slotKey
  const system = window.editor?.selectedSystem
  const targetComponentUuid = targetUuids?.[0]
  if (!system || !targetComponentUuid) return undefined

  for (const inverter of system.inverters?.() || []) {
    const inverterLevelSlots = getSlotsByLocation(inverter.uuid, system) || []
    for (const slot of inverterLevelSlots) {
      if (slot.type !== 'dc_isolator') continue
      const key = getSlotKey(slot)
      const components = system.getComponentsForSlot(key) || []
      if (components.some((c) => c?.uuid === targetComponentUuid)) return key
    }

    for (const mppt of inverter.mppts?.() || []) {
      const mpptLevelSlots = getSlotsByLocation(mppt.uuid, system) || []
      for (const slot of mpptLevelSlots) {
        if (slot.type !== 'dc_isolator') continue
        const key = getSlotKey(slot)
        const components = system.getComponentsForSlot(key) || []
        if (components.some((c) => c?.uuid === targetComponentUuid)) return key
      }

      for (const str of mppt.strings?.() || []) {
        const stringLevelSlots = getSlotsByLocation(str.uuid, system) || []
        for (const slot of stringLevelSlots) {
          if (slot.type !== 'dc_isolator') continue
          const key = getSlotKey(slot)
          const components = system.getComponentsForSlot(key) || []
          if (components.some((c) => c?.uuid === targetComponentUuid)) return key
        }
      }
    }
  }

  return undefined
}

const buildLocalOtherSpecsByCode = (): Map<string, LocalComponent> => {
  const specs: LocalComponent[] = window.AccountHelper?.getComponentOtherSpecsAvailable?.() ?? []
  const byCode = new Map<string, LocalComponent>()
  for (const spec of specs) {
    if (!spec?.code) continue
    byCode.set(String(spec.code), spec)
  }
  return byCode
}

const extractRatingMap = (component: FetchedComponent, localByCode: Map<string, LocalComponent>): RatingMap | null => {
  const componentDataParsed = parseJsonSafe(component?.data, null) as FetchedComponent | null
  const ratingMapRaw =
    component?.voltage_to_current_rating ??
    componentDataParsed?.voltage_to_current_rating ??
    component?.other?.voltage_to_current_rating
  let ratingMap: RatingMap | string | null | undefined =
    typeof ratingMapRaw === 'string' ? (parseJsonSafe(ratingMapRaw, null) as RatingMap | null) : ratingMapRaw

  if (!ratingMap || typeof ratingMap !== 'object') {
    const localComponent = localByCode.get(String(component?.code || ''))
    const localRatingRaw = localComponent?.voltage_to_current_rating
    ratingMap =
      typeof localRatingRaw === 'string' ? (parseJsonSafe(localRatingRaw, null) as RatingMap | null) : localRatingRaw
  }

  return ratingMap && typeof ratingMap === 'object' ? ratingMap : null
}

export const dcIsolatorCompatibilityPostFetchGuard: PostFetchGuard = ({
  componentsData,
  componentTypes,
  allFilterValues,
  slotKey,
  targetUuids,
}) => {
  const isDcIsolatorFlow =
    componentTypes?.length === 1 && componentTypes[0] === 'isolator' && allFilterValues?.current_type === 'dc'
  const requiredValue = allFilterValues?.voltage_to_current_rating
  if (!isDcIsolatorFlow || typeof requiredValue !== 'string' || !requiredValue.includes(',')) {
    return componentsData || []
  }

  const resolvedSlotKey = resolveSlotKeyFromTarget({ slotKey, targetUuids })
  if (!resolvedSlotKey || !resolvedSlotKey.startsWith('dc_isolator_')) return componentsData || []

  const attachUuid = resolvedSlotKey.replace('dc_isolator_', '')
  const attachObject = window.editor?.objectByUuid?.(attachUuid)
  const strings = attachObject?.type === 'OsString' ? [attachObject] : attachObject?.children || []
  if (!strings.length) return componentsData || []

  const localByCode = buildLocalOtherSpecsByCode()

  return (componentsData || []).filter((component: FetchedComponent) => {
    const ratingMap = extractRatingMap(component, localByCode)
    if (!ratingMap) return false
    const compatibility = getDCIsolatorCompatibility(
      { ...component, voltage_to_current_rating: ratingMap } as unknown as OtherComponentType,
      strings
    )
    return compatibility.compatibility !== 'bad' && !compatibility?.data?.error
  })
}
